import React from 'react';
import { ShieldCheck, Link2, Eye, ArrowRight } from 'lucide-react';

/* Referral transparency: tracking panel, attribution flow, trust panel. */

export function TrackingPanel({ isEs, refCode, status = 'active' }) {
  const ROWS = [
    [isEs ? 'Código de Partner' : 'Partner Code', refCode, true],
    [isEs ? 'Duración de la cookie' : 'Cookie Duration', isEs ? '90 días' : '90 Days'],
    ['Tracking', 'First Click Attribution'],
    [isEs ? 'Canales' : 'Tracking Channel', 'Web · WhatsApp · Email · QR'],
    [isEs ? 'Estado actual' : 'Current Status', status === 'active' ? (isEs ? '🟢 Activo' : '🟢 Active') : status],
  ];
  return (
    <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="partner-tracking-panel">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
        <Link2 className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Tu atribución — 100% transparente' : 'Your attribution — 100% transparent'}
      </p>
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-5 gap-2">
        {ROWS.map(([l, v, hl]) => (
          <div key={l} className={`rounded-xl border p-3 ${hl ? 'border-[#F77F11]/40 bg-orange-50/60' : 'border-neutral-100 bg-neutral-50'}`}>
            <p className="text-[9.5px] uppercase tracking-wider text-neutral-400 font-bold">{l}</p>
            <p className={`mt-1 text-[13px] font-bold ${hl ? 'text-[#d8651a] font-mono' : 'text-neutral-800'}`}>{v}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[12px] text-neutral-500 font-medium border-t border-neutral-100 pt-3" data-testid="partner-tracking-tagline">
        {isEs
          ? 'Cada clic. Cada scan. Cada reporte. Cada propuesta. Cada contrato. Cada pago recurrente. Todo rastreado y visible en tu dashboard.'
          : 'Every click. Every scan. Every report. Every proposal. Every contract. Every recurring payment. Fully tracked and visible in your dashboard.'}
      </p>
    </div>
  );
}

const FLOW = (isEs) => isEs
  ? ['Tu enlace', 'Scan del restaurante', 'Reporte desbloqueado', 'Propuesta', 'CRM', 'Equipo de ventas', 'Contrato', 'Comisión recurrente']
  : ['Partner Link', 'Restaurant Scan', 'Unlock Report', 'Proposal', 'CRM', 'Sales Team', 'Contract', 'Recurring Commission'];

export function AttributionFlow({ isEs }) {
  const steps = FLOW(isEs);
  return (
    <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="partner-attribution-flow">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
        <Eye className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Cómo se genera tu comisión — nada se pierde' : 'How your commission is generated — nothing gets lost'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-y-2">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-[#F77F11] mx-1.5 shrink-0" aria-hidden />}
            <span className={`rounded-full border px-3 py-1.5 text-[12px] font-bold whitespace-nowrap ${i === 0 || i === steps.length - 1 ? 'bg-[#F77F11] border-[#F77F11] text-white' : 'bg-neutral-50 border-neutral-200 text-neutral-700'}`}>
              {s}
            </span>
          </React.Fragment>
        ))}
      </div>
      <p className="mt-3 text-[12px] text-neutral-500">
        {isEs
          ? 'Todo scan lanzado desde tu enlace hereda automáticamente tu ID, código, campaña, canal, UTM y cookie de 90 días. El recorrido completo queda vinculado a ti.'
          : 'Every scan launched from your link automatically inherits your ID, code, campaign, channel, UTM and 90-day cookie. The entire journey stays linked to you.'}
      </p>
    </div>
  );
}

export function TrustPanel({ isEs }) {
  const bullets = isEs
    ? ['Cada visitante se rastrea.', 'Cada scan queda registrado.', 'Cada reporte desbloqueado te pertenece.',
       'Cada contrato generado por tu referido paga comisiones recurrentes.', 'Nada está oculto. Todo es visible.']
    : ['Every visitor is tracked.', 'Every scan is recorded.', 'Every unlocked report belongs to you.',
       'Every contract generated from your referral earns recurring commissions.', 'Nothing is hidden. Everything is visible.'];
  return (
    <div className="mt-4 rounded-2xl bg-neutral-950 text-white p-6 relative overflow-hidden" data-testid="partner-trust-panel">
      <div aria-hidden className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#F77F11]/15 blur-[70px] pointer-events-none" />
      <div className="relative">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#FF9B4A] font-bold">
          <ShieldCheck className="w-4 h-4" /> {isEs ? 'Por qué el tracking de Kitxens es justo' : 'Why Kitxens Tracking is Fair'}
        </p>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {bullets.map((b) => (
            <p key={b} className="text-[13.5px] text-neutral-300 flex items-start gap-2"><span className="text-emerald-400 mt-px">✓</span> {b}</p>
          ))}
        </div>
        <p className="mt-3 text-[12.5px] text-neutral-500 italic">
          {isEs ? 'La transparencia es uno de nuestros mayores diferenciadores.' : 'Transparency is one of our biggest differentiators.'}
        </p>
      </div>
    </div>
  );
}

/* Journey stages for each referral — derived from real timeline events */
export const JOURNEY_STAGES = (isEs) => [
  ['lead_registered', isEs ? 'Lead registrado' : 'Lead registered'],
  ['contacted', isEs ? 'Contactado' : 'Contacted'],
  ['proposal_sent', isEs ? 'Propuesta enviada' : 'Proposal sent'],
  ['demo', isEs ? 'Discovery call / demo' : 'Discovery Call / demo'],
  ['follow_up', isEs ? 'Seguimiento' : 'Follow-up'],
  ['status_customer', isEs ? 'Cerrado — Cliente 🎉' : 'Closed Won 🎉'],
];

export function ReferralJourney({ events, isEs }) {
  const done = new Set((events || []).map((e) => e.type));
  return (
    <div className="flex flex-wrap gap-1.5 px-4 pt-2" data-testid="referral-journey">
      {JOURNEY_STAGES(isEs).map(([k, l]) => (
        <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${done.has(k) ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-neutral-50 border-neutral-200 text-neutral-400'}`}>
          {done.has(k) ? '✓' : '·'} {l}
        </span>
      ))}
    </div>
  );
}
